import { useCallback, useEffect, useRef } from "react";
import { connectWebSocket } from "../components/Chat/websocket";

const useChatThreadSubscriptions = ({
  activeThread,
  activeThreadRef,
  loadingResponseByThreadUuid,
  pushAnalysisTraceEventForThread,
  pushDataProcessForThread,
  setAnimatedItems,
  setDisplayUpdatedInfo,
  setLatestTickerPrices,
  setThreadLiveTextOutputting,
  setThreadLoadingPhaseDetail,
  setThreadLoadingPhaseMessage,
  setThreadQuestionOnDemand,
  setThreadShowLoadingWaitingMessage,
  setUseFullWidth,
  setWatchlistData,
  showLoadingWaitingMessageByThreadUuid,
  updateThreadMessages,
  useFullWidth,
  userSessionId,
  websocketActiveChartRef,
  websocketActiveTickerId,
  websocketSubscriptionRef,
}) => {
  const threadSubscriptionsRef = useRef(new Map());
  const primarySocketMetaRef = useRef({
    threadUuid: null,
    tickerId: null,
    sessionId: null,
  });

  const buildWebsocketHandlers = useCallback(
    (threadUuid, isPrimary) => {
      const noop = () => {};
      return {
        setQuestionOnDemand: (val) =>
          setThreadQuestionOnDemand(threadUuid, val),
        setMessageThreads: (updater) =>
          updateThreadMessages(threadUuid, updater),
        showLoadingWaitingMessage:
          showLoadingWaitingMessageByThreadUuid?.[threadUuid] ?? null,
        setShowLoadingWaitingMessage: (val) =>
          setThreadShowLoadingWaitingMessage(threadUuid, val),
        setLoadingPhaseMessage: (val) =>
          setThreadLoadingPhaseMessage(threadUuid, val),
        setLoadingPhaseDetail: (val) =>
          setThreadLoadingPhaseDetail(threadUuid, val),
        pushDataProcess: (payload) =>
          pushDataProcessForThread(threadUuid, payload),
        pushAnalysisTraceEvent: (payload) =>
          pushAnalysisTraceEventForThread(threadUuid, payload),
        setLiveTextOutputting: (val) =>
          setThreadLiveTextOutputting(threadUuid, val),
        setLatestTickerPrices: isPrimary ? setLatestTickerPrices : noop,
        setDisplayUpdatedInfo: isPrimary ? setDisplayUpdatedInfo : noop,
        setWatchlistData: isPrimary ? setWatchlistData : noop,
        setAnimatedItems: isPrimary ? setAnimatedItems : noop,
      };
    },
    [
      pushAnalysisTraceEventForThread,
      pushDataProcessForThread,
      setAnimatedItems,
      setDisplayUpdatedInfo,
      setLatestTickerPrices,
      setThreadLiveTextOutputting,
      setThreadLoadingPhaseDetail,
      setThreadLoadingPhaseMessage,
      setThreadQuestionOnDemand,
      setThreadShowLoadingWaitingMessage,
      setWatchlistData,
      showLoadingWaitingMessageByThreadUuid,
      updateThreadMessages,
    ],
  );

  const ensureThreadSubscription = useCallback(
    (threadUuid, options = {}) => {
      if (!threadUuid) return null;
      if (!userSessionId) return null;
      const { force = false, setAsPrimary = null } = options || {};
      const tabIdOverride =
        options?.tabIdOverride || options?.tab_id || options?.tabId || null;
      const streamUserUuidOverride =
        options?.streamUserUuidOverride ||
        options?.stream_user_uuid ||
        options?.streamUserUuid ||
        null;
      const subs = threadSubscriptionsRef.current;
      const existing = subs.get(threadUuid);
      const existingMatches =
        existing &&
        existing.sessionId === userSessionId &&
        !["disposed", "rejected"].includes(existing.subscription?._inciteState) &&
        (existing.tabIdOverride || null) === (tabIdOverride || null) &&
        (existing.streamUserUuidOverride || null) ===
          (streamUserUuidOverride || null);
      if (existing && !force && existingMatches) {
        const primary = typeof setAsPrimary === "boolean" ? setAsPrimary : activeThreadRef.current?.threadUuid === threadUuid;
        existing.updateHandlers?.(buildWebsocketHandlers(threadUuid, primary), primary);
        return existing;
      }
      if (existing && (force || !existingMatches)) {
        try {
          existing.cleanup?.();
        } catch {
          // Cleanup is best-effort; a stale socket should not block resubscribe.
        }
        subs.delete(threadUuid);
      }

      const isPrimary =
        typeof setAsPrimary === "boolean"
          ? setAsPrimary
          : activeThreadRef.current?.threadUuid === threadUuid;
      const handlers = buildWebsocketHandlers(threadUuid, isPrimary);

      const entry = connectWebSocket(
        handlers.setQuestionOnDemand,
        userSessionId,
        websocketSubscriptionRef,
        handlers.setMessageThreads,
        handlers.showLoadingWaitingMessage,
        handlers.setShowLoadingWaitingMessage,
        useFullWidth,
        setUseFullWidth,
        handlers.setLoadingPhaseMessage,
        handlers.setLoadingPhaseDetail,
        handlers.pushDataProcess,
        handlers.pushAnalysisTraceEvent,
        handlers.setLiveTextOutputting,
        handlers.setLatestTickerPrices,
        handlers.setDisplayUpdatedInfo,
        handlers.setWatchlistData,
        handlers.setAnimatedItems,
        { threadUuid },
        websocketActiveChartRef,
        { setAsPrimary: isPrimary, tabIdOverride, streamUserUuidOverride },
      );

      entry.tabIdOverride = tabIdOverride || null;
      entry.streamUserUuidOverride = streamUserUuidOverride || null;
      entry.sessionId = userSessionId;
      subs.set(threadUuid, entry);
      return entry;
    },
    [
      activeThreadRef,
      buildWebsocketHandlers,
      setUseFullWidth,
      useFullWidth,
      userSessionId,
      websocketActiveChartRef,
      websocketSubscriptionRef,
    ],
  );

  const moveThreadSubscription = useCallback(
    (fromThreadUuid, toThreadUuid, options = {}) => {
      if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid) {
        return null;
      }

      const subs = threadSubscriptionsRef.current;
      const existing = subs.get(fromThreadUuid);
      if (existing) {
        try {
          existing.cleanup?.();
        } catch {
          // Cleanup is best-effort; the real thread subscription is created below.
        }
        subs.delete(fromThreadUuid);
      }

      const shouldBePrimary = Boolean(options?.setAsPrimary);
      const entry = ensureThreadSubscription(toThreadUuid, {
        setAsPrimary: shouldBePrimary,
      });
      if (shouldBePrimary && entry?.subscription) {
        websocketSubscriptionRef.current = entry.subscription;
        primarySocketMetaRef.current = {
          ...(primarySocketMetaRef.current || {}),
          threadUuid: toThreadUuid,
          sessionId:
            userSessionId ||
            primarySocketMetaRef.current?.sessionId ||
            null,
        };
      }
      return entry;
    },
    [ensureThreadSubscription, userSessionId, websocketSubscriptionRef],
  );

  useEffect(() => {
    const threadId = activeThread?.threadUuid;
    if (!threadId || !userSessionId) return;
    const existingEntry = threadSubscriptionsRef.current.get(threadId);
    const existingUsesOverride =
      Boolean(existingEntry?.tabIdOverride) ||
      Boolean(existingEntry?.streamUserUuidOverride);
    if (existingEntry?.sessionId === userSessionId && existingUsesOverride && loadingResponseByThreadUuid?.[threadId]) {
      existingEntry.updateHandlers?.(buildWebsocketHandlers(threadId, true), true);
      if (existingEntry.subscription) {
        websocketSubscriptionRef.current = existingEntry.subscription;
      }
      primarySocketMetaRef.current = {
        threadUuid: threadId,
        tickerId: websocketActiveTickerId,
        sessionId: userSessionId,
      };
      return;
    }
    const entry = ensureThreadSubscription(threadId, {
      setAsPrimary: true,
    });
    if (entry?.subscription) {
      websocketSubscriptionRef.current = entry.subscription;
    }
    primarySocketMetaRef.current = {
      threadUuid: threadId,
      tickerId: websocketActiveTickerId,
      sessionId: userSessionId,
    };
  }, [
    activeThread?.threadUuid,
    buildWebsocketHandlers,
    ensureThreadSubscription,
    loadingResponseByThreadUuid,
    userSessionId,
    websocketActiveTickerId,
    websocketSubscriptionRef,
  ]);

  useEffect(() => {
    const subs = threadSubscriptionsRef.current;
    return () => {
      subs.forEach((entry) => {
        try {
          entry.cleanup?.();
        } catch {
          // Cleanup is best-effort during component teardown.
        }
      });
      subs.clear();
    };
  }, []);

  useEffect(() => {
    const subs = threadSubscriptionsRef.current;
    if (!subs?.size) return;
    const activeId = activeThread?.threadUuid ?? null;
    subs.forEach((entry, threadUuid) => {
      if (threadUuid === activeId) return;
      if (loadingResponseByThreadUuid?.[threadUuid] && entry.sessionId === userSessionId) {
        entry.updateHandlers?.(buildWebsocketHandlers(threadUuid, false), false);
        return;
      }
      try {
        entry.cleanup?.();
      } catch {
        // Cleanup is best-effort for inactive background subscriptions.
      }
      subs.delete(threadUuid);
    });
  }, [loadingResponseByThreadUuid, activeThread?.threadUuid, userSessionId, buildWebsocketHandlers]);

  return {
    ensureThreadSubscription,
    moveThreadSubscription,
  };
};

export default useChatThreadSubscriptions;
